import React from 'react'
import { HiCog6Tooth, HiArrowTrendingUp, HiClock } from 'react-icons/hi2'

interface PredictionParametersProps {
  parameters: {
    draw_threshold: number
    shrinkage_k: number
    normalization_scale: number
    composite_weight: number
    optimization_accuracy: number | null
    optimized_at: string | null
  }
  isOptimized: boolean
}

const PredictionParameters: React.FC<PredictionParametersProps> = ({
  parameters,
  isOptimized
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'N/A'
    }
  }

  return (
    <div className="bg-gray-800/50 rounded-lg p-4 sm:p-6 border border-gray-700">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <HiCog6Tooth className="h-5 w-5 text-orange-400" />
        <h3 className="text-base sm:text-lg font-semibold text-white">Prediction Engine Parameters</h3>
        {isOptimized && (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-green-900/30 text-green-300 text-[11px] sm:text-xs font-medium rounded-full">
            <HiArrowTrendingUp className="h-3 w-3" />
            <span>Optimized</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-900/50 rounded p-3">
          <div className="text-[11px] sm:text-xs text-gray-400 mb-1">Draw Threshold</div>
          <div className="text-base sm:text-lg font-mono text-white">
            {parameters.draw_threshold.toFixed(6)}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
            Threshold for draw classification
          </div>
        </div>

        <div className="bg-gray-900/50 rounded p-3">
          <div className="text-[11px] sm:text-xs text-gray-400 mb-1">Shrinkage K</div>
          <div className="text-base sm:text-lg font-mono text-white">
            {parameters.shrinkage_k.toFixed(2)}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
            Empirical Bayes shrinkage parameter
          </div>
        </div>

        <div className="bg-gray-900/50 rounded p-3">
          <div className="text-[11px] sm:text-xs text-gray-400 mb-1">Normalization Scale</div>
          <div className="text-base sm:text-lg font-mono text-white">
            {parameters.normalization_scale.toFixed(2)}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
            Scale factor for cumulative normalization
          </div>
        </div>

        <div className="bg-gray-900/50 rounded p-3">
          <div className="text-[11px] sm:text-xs text-gray-400 mb-1">Composite Weight</div>
          <div className="text-base sm:text-lg font-mono text-white">
            {parameters.composite_weight.toFixed(4)}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
            Weight for composite score calculation
          </div>
        </div>
      </div>

      {isOptimized && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400">
            <HiClock className="h-4 w-4" />
            <span>Optimized: {formatDate(parameters.optimized_at)}</span>
            {parameters.optimization_accuracy !== null && (
              <>
                <span className="mx-2">•</span>
                <span>
                  Accuracy: {(parameters.optimization_accuracy * 100).toFixed(2)}%
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {!isOptimized && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="text-xs sm:text-sm text-gray-400 italic">
            Using default parameters (not yet optimized)
          </div>
        </div>
      )}
    </div>
  )
}

export default PredictionParameters


